import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";

const ApprovalRulesets = () => {
  const [rulesets, setRulesets] = useState([]);
  const [approvals, setApprovals] = useState({});
  const [selectedRuleset, setSelectedRuleset] = useState("");
  const [loading, setLoading] = useState(false);

  const commentTemplate = `
Review Checklist:
- [ ] Fraud checks verified: 
- [ ] Compliance checks verified: 
- [ ] Any additional comments: 
  `;

  // Initial data fetch for rulesets
  useEffect(() => {
    console.log("Initial useEffect: Fetching rule sets...");
    fetchRuleSets();
  }, []);

  // Update approvals state when a ruleset is selected
  useEffect(() => {
    if (selectedRuleset && approvals[selectedRuleset] === undefined) {
      const selected = rulesets.find((r) => r.id === Number(selectedRuleset));
      if (selected) {
        console.log("Initializing approvals for selected ruleset:", selectedRuleset);
        setApprovals((prev) => ({
          ...prev,
          [selected.id]: {
            approver1_comment: selected.approver1_comment || commentTemplate,
            approver2_comment: selected.approver2_comment || commentTemplate,
            approver1_approved: selected.approver1_approved || false,
            approver2_approved: selected.approver2_approved || false,
          },
        }));
      }
    }
  }, [selectedRuleset, rulesets]);
  
  const getSubscriberId = async () => {
    try {
      const token = Cookies.get("access_token") || localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      if (!token) return null;

      const payload = JSON.parse(atob(token.split(".")[1]));
      const tenantId = payload.tenant_id || payload.subscriber_name;
      if (!tenantId) return null;

      const response = await axios.get(` /composite-backend/internal/ui/rules/get-subscriber-id?tenant_id=${tenantId}`);
      return response.data?.subscriber_id || null;
    } catch (error) {
      return null;
    }
  };

const fetchRuleSets = async () => {
  try {
    const subscriberId = await getSubscriberId();
    if (!subscriberId) return;
    console.log("Fetching rulesets from the backend...");
    
    const token = Cookies.get("access_token") || localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    if (!token) {
      console.error("❌ No access token found.");
      return;
    }

    const response = await axios.get(` /composite-backend/internal/ui/rules/list-underreview-rulesets?subscriber_id=${subscriberId}`, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    console.log("Raw response from backend:", JSON.stringify(response.data, null, 2));

    const underReviewRulesets = response.data;
    console.log("✅ Retrieved Rulesets:", JSON.stringify(underReviewRulesets, null, 2));

    setRulesets(underReviewRulesets);

    // ✅ Ensure `approvals` state updates correctly with approval status
    const updatedApprovals = {};
    underReviewRulesets.forEach((ruleset) => {
      if (ruleset.id) {
        updatedApprovals[ruleset.id] = {
          approver1_comment: ruleset.approver1_comment || "",
          approver2_comment: ruleset.approver2_comment || "",
          approver1_approved: ruleset.approver1_approved ?? false,  // ✅ Ensure boolean value is fetched correctly
          approver2_approved: ruleset.approver2_approved ?? false,
        };
      } else {
        console.warn("⚠️ Ruleset missing id:", ruleset);
      }
    });

    setApprovals(updatedApprovals);
    console.log("✅ Updated Approvals State:", JSON.stringify(updatedApprovals, null, 2));
  } catch (error) {
    console.error("❌ Error fetching rule sets:", error);
  }
};






   const handleRulesetChange = (event) => {
    const selectedId = event.target.value ? Number(event.target.value) : "";
    setSelectedRuleset(selectedId);
  };

  // Handle comment change for an approver
  const handleCommentChange = (rulesetId, approver, comment) => {
    console.log("Updating approvals for rulesetId:", rulesetId, "Approver:", approver);
    setApprovals((prev) => ({
      ...prev,
      [rulesetId]: {
        ...prev[rulesetId],
        [`${approver}_comment`]: comment,
        [`${approver}_approved`]: prev[rulesetId]?.[`${approver}_approved`] ?? false, // Preserve approved status
      },
    }));
  };

 const handleSaveComments = async (approver) => {
    console.log("✅ Available rulesets:", rulesets.map(r => r.id));

    const ruleset = rulesets.find((r) => Number(r.id) === Number(selectedRuleset));

    if (!ruleset) {
      console.error(`❌ Selected ruleset ID ${selectedRuleset} not found in rulesets.`, rulesets);
      alert("❌ Selected ruleset not found. Please refresh and try again.");
      return;
    }

    const comments = approvals[ruleset.id]?.[`${approver}_comment`];

    console.log("✅ Selected Ruleset:", ruleset);
    console.log("✅ Approvals State:", approvals);

    if (!comments || comments === commentTemplate) {
      alert(`❌ No comments entered for ${approver === "approver1" ? "Approver 1" : "Approver 2"}`);
      return;
    }

    try {
      const subscriberId = await getSubscriberId();
      if (!subscriberId) return;

      const payload = {
        ruleset_id: ruleset.id,
        approver,
        comment: comments,
        subscriber_id: subscriberId,
      };

      console.log("✅ Payload being sent to backend:", payload);

      await axios.post("/composite-backend/internal/ui/rules/update-approver", payload);

      alert("✅ Comments saved successfully!");
    } catch (error) {
      console.error("❌ Error saving comments:", error);
      alert("Failed to save comments. Please try again.");
    }
  };



 const handleApprove = async (approver) => {
    const ruleset = rulesets.find((r) => r.id === Number(selectedRuleset));
    const comments = approvals[ruleset?.id]?.[`${approver}_comment`];

    if (!comments || !ruleset || comments === commentTemplate) {
      alert(`Approver ${approver === "approver1" ? "1" : "2"} must save comments before approval.`);
      return;
    }

    setLoading(true);

    try {
      const subscriberId = await getSubscriberId();
      if (!subscriberId) return;
      
      const payload = {
        ruleset_id: ruleset.id,
        approver,
        comment: comments,
        approved: true,
        subscriber_id: subscriberId,
      };

      console.log("Approval Payload:", payload);
      await axios.post("/composite-backend/internal/ui/rules/update-approver", payload);
      fetchRuleSets(); // Refresh rulesets
      alert(`Approval updated successfully for ${approver}`);
    } catch (error) {
      console.error("Error approving ruleset:", error);
      alert("Failed to approve ruleset. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (approver) => {
    const ruleset = rulesets.find((r) => r.id === Number(selectedRuleset));
    const comments = approvals[ruleset?.id]?.[`${approver}_comment`];

    if (!comments || !ruleset) {
      alert(`Approver ${approver === "approver1" ? "1" : "2"} must enter comments before rejecting.`);
      return;
    }

    setLoading(true);

    try {
      const subscriberId = await getSubscriberId();
      if (!subscriberId) return;
      
      const payload = {
        ruleset_id: ruleset.id,
        approver,
        comment: comments,
        approved: false,
        status: "Draft",
        subscriber_id: subscriberId,
      };

      console.log("Reject Payload:", payload);
      await axios.post("/composite-backend/internal/ui/rules/update-approver", payload);
      fetchRuleSets(); // Refresh rulesets
      alert(`Ruleset ${ruleset.ruleset_name} has been moved to Draft mode.`);
    } catch (error) {
      console.error("Error rejecting ruleset:", error);
      alert("Failed to reject ruleset. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div style={{ fontFamily: "Arial, sans-serif" }}>
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#007BFF",
          padding: "16px",
          color: "#FFF",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src="/assets/b4d48a3e-5b5a-4392-ab30-df41c0a4471a.jpg"
            alt="Idenza Logo"
            style={{ width: "50px", marginRight: "10px" }}
          />
          <h1 style={{ margin: "0" }}>Idenza</h1>
        </div>
        <h2 style={{ textAlign: "center", flexGrow: 1 }}>Approve Rulesets</h2>
      </div>

      {/* Ruleset Selection */}
      <div style={{ padding: "24px" }}>
        <label htmlFor="rulesetSelect" style={{ marginRight: "10px" }}>
          Select Ruleset to Approve:
        </label>
        <select
          id="rulesetSelect"
          value={selectedRuleset}
          onChange={handleRulesetChange}
          style={{
            padding: "8px",
            width: "50%",
            borderRadius: "4px",
            border: "1px solid #D1D5DB",
          }}
        >
          <option value="">-- Select a Ruleset --</option>
          {rulesets.map((ruleset) => (
            <option key={ruleset.id} value={ruleset.id}>
              {ruleset.ruleset_name}
            </option>
          ))}
        </select>
      </div>

      {/* Main Content */}
      {selectedRuleset && (
        <div style={{ padding: "24px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f3f4f6" }}>
                <th
                  style={{
                    border: "1px solid #e5e7eb",
                    padding: "12px",
                    textAlign: "center",
                    width: "20%",
                  }}
                >
                  Approver
                </th>
                <th
                  style={{
                    border: "1px solid #e5e7eb",
                    padding: "12px",
                    textAlign: "center",
                    width: "50%",
                  }}
                >
                  Comments
                </th>
                <th
                  style={{
                    border: "1px solid #e5e7eb",
                    padding: "12px",
                    textAlign: "center",
                    width: "30%",
                  }}
                >
                  Actions
                </th>
                <th
                  style={{
                    border: "1px solid #e5e7eb",
                    padding: "12px",
                    textAlign: "center",
                    width: "20%",
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {["approver1", "approver2"].map((approver) => (
                <tr key={approver}>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "12px",
                      textAlign: "center",
                    }}
                  >
                    {approver === "approver1" ? "Approver 1" : "Approver 2"}
                  </td>
                  <td style={{ border: "1px solid #e5e7eb", padding: "12px" }}>
                    <textarea
                      placeholder={`${approver === "approver1" ? "Approver 1" : "Approver 2"} Comment`}
                      value={
                        approvals[selectedRuleset]?.[`${approver}_comment`] ||
                        commentTemplate
                      }
                      onChange={(e) =>
                        handleCommentChange(
                          selectedRuleset,
                          approver,
                          e.target.value
                        )
                      }
                      style={{
                        width: "98%",
                        height: "120px",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #D1D5DB",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "12px",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => handleSaveComments(approver)}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#FFA500",
                        color: "#FFF",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        marginBottom: "8px",
                      }}
                    >
                      Save Comments
                    </button>
                    <button
                      onClick={() => handleApprove(approver)}
                      disabled={
                        loading ||
                        approvals[selectedRuleset]?.[`${approver}_approved`] ||
                        !approvals[selectedRuleset]?.[`${approver}_comment`] ||
                        approvals[selectedRuleset]?.[`${approver}_comment`] === commentTemplate
                      }
                      style={{
                        padding: "8px 16px",
                        backgroundColor:
                          approvals[selectedRuleset]?.[`${approver}_approved`] ? "#808080" : approver === "approver1" ? "#10B981" : "#1E40AF",
                        color: "#FFF",
                        border: "none",
                        borderRadius: "8px",
                        cursor: loading || approvals[selectedRuleset]?.[`${approver}_approved`] ? "not-allowed" : "pointer",
                      }}
                    >
                      Approve as {approver === "approver1" ? "Approver 1" : "Approver 2"}
                    </button>
                    <button
                      onClick={() => handleReject(approver)}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#DC2626",
                        color: "#FFF",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        marginTop: "8px",
                      }}
                    >
                      Reject as {approver === "approver1" ? "Approver 1" : "Approver 2"}
                    </button>
                  </td>
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "12px",
                      textAlign: "center",
                    }}
                  >
                    {approvals[selectedRuleset]?.[`${approver}_approved`] ? (
                      <span style={{ color: "green", fontWeight: "bold" }}>Approved</span>
                    ) : (
                      <span style={{ color: "red", fontWeight: "bold" }}>Unapproved</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Section */}
      <div
        style={{
          backgroundColor: "#007BFF",
          color: "#FFF",
          textAlign: "center",
          padding: "16px",
        }}
      >
        <p>Idenza Ruleset Management © 2024</p>
      </div>
    </div>
  );
};

export default ApprovalRulesets;

